import React, { useEffect, useMemo, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Combobox,
  Icon,
  PagingSidePanelType,
  SidePanel,
  Text,
  Tooltip,
} from '@node-space/ui-components'
import { ellipseMiddleText, isEmpty } from '@node-space/utils'
import { appBannersId } from 'components/app/AppBanners'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { Channel } from 'types/channels'
import { CurrencyInterface } from 'types/wallets'
import { highlightRowBasedOnId } from 'utils/table/table'
import { getSelectedAddress } from '../utils'
import { QRCode } from './QRCode'

export interface ChannelDetailsProps {
  isOpen: boolean
  onClose: () => void
  paging: PagingSidePanelType
  channelDetails: Channel
  currencies?: CurrencyInterface[]
}

export const ChannelsSidePanel = ({
  isOpen,
  onClose,
  paging,
  channelDetails,
  currencies,
}: ChannelDetailsProps) => {
  const { t } = useTranslation()
  const bannerSpacing = useComponentHeight(appBannersId)
  const [selectedProtocol, setSelectedProtocol] = useState(channelDetails?.protocol)

  const selectedAddress = getSelectedAddress(channelDetails, selectedProtocol)

  const isMultiProtocolCurrency = !isEmpty(channelDetails?.alternatives)

  const selectedCurrencyNetwork = currencies?.find(
    network => network?.code === channelDetails?.payCurrency
  )

  const protocolItems = useMemo(() => {
    if (!isMultiProtocolCurrency) return []
    return [
      { leftText: channelDetails?.protocol, value: channelDetails?.protocol },
      ...(channelDetails?.alternatives?.map(p => ({ leftText: p?.protocol, value: p?.protocol })) ||
        []),
    ]
  }, [isMultiProtocolCurrency, channelDetails])

  useEffect(() => {
    setSelectedProtocol(channelDetails?.protocol)
  }, [channelDetails, isOpen])

  return (
    <SidePanel
      id="side-panel-channel-details"
      externalStyle={{ top: `${bannerSpacing}px` }}
      isOpen={isOpen}
      paging={paging}
      onClose={onClose}
      activeRowId={channelDetails?.reference}
      onActiveRowChange={highlightRowBasedOnId}
      header={
        <>
          {!!channelDetails?.contact && (
            <Box flex gap={16} paddingB={12}>
              <Avatar size="large" type="entity" name={channelDetails?.contact?.name ?? ''} />
              <Box>
                <Text color="subdued">{channelDetails?.contact?.name}</Text>
                <Text variant="heading-2">{channelDetails?.contact?.description}</Text>
              </Box>
            </Box>
          )}

          <Box>
            <Text color="subdued">{t('payments.channelPayments.channelLink')}</Text>
            <Box flex className="flex-row justify-between items-center">
              <Text>{ellipseMiddleText(channelDetails?.redirectUrl, 30, 6, '*')}</Text>
              <Box flex className="flex items-center gap-2">
                <Button
                  iconElement={<Icon name="ArrowSquareOut" />}
                  variant="ghost"
                  noPadding
                  onClick={() =>
                    window.open(channelDetails?.redirectUrl, '_blank', 'noopener,noreferrer')
                  }
                />
                <CopyToClipboard text={channelDetails?.redirectUrl}>
                  <Box className="cursor-pointer flex items-center" color="action-primary">
                    <Tooltip position="top" bodyContent={channelDetails?.redirectUrl} type="simple">
                      <Button iconElement={<Icon name="Copy" />} variant="ghost" noPadding />
                    </Tooltip>
                  </Box>
                </CopyToClipboard>
              </Box>
            </Box>
          </Box>
          <Box className="mt-2">
            <Text color="subdued">{t('wallet')}</Text>
            <Box flex className="flex-row items-center gap-2">
              <Text>{channelDetails?.walletDescription}</Text>
            </Box>
            <Box className="mt-2">
              <Text color="subdued">{t('payments.channelPayments.payCurrency')}</Text>
              <Box flex className="flex-row items-center gap-2">
                <Avatar size="small" name={channelDetails?.payCurrency ?? ''} />
                <Text>{channelDetails?.payCurrency}</Text>
              </Box>
            </Box>
          </Box>
        </>
      }
      body={
        <Box>
          <Box className="flex flex-col">
            <Text variant="heading-2">{t('payments.channelPayments.channelDetails')}</Text>
            <Text variant="body-label">{t('payments.channelPayments.scanQRCode')}</Text>
            <Box className="mt-6">
              {isMultiProtocolCurrency && (
                <Combobox
                  key={`protocol-selector-${channelDetails?.reference}-${isOpen}`}
                  id="select-protocol"
                  label={t('payments.channelPayments.selectProtocol')}
                  selectPlaceholder={t('payments.channelPayments.selectProtocol')}
                  defaultValue={selectedProtocol}
                  filter={false}
                  onChange={setSelectedProtocol}
                  items={protocolItems}
                />
              )}
            </Box>
            <Box className="flex flex-col items-center mt-6">
              <QRCode
                displayAddress={selectedAddress}
                selectedCurrencyCode={channelDetails?.payCurrency?.toLowerCase()}
                protocol={
                  selectedProtocol?.toLowerCase() || channelDetails?.payCurrency?.toLowerCase()
                }
                isMultiProtocolCurrency={isMultiProtocolCurrency}
              />
            </Box>
          </Box>
          <Box flex className="flex-row justify-between items-center mt-6">
            <Text color="subdued">
              {t('payments.channelPayments.sendWalletAddress', {
                currencyCode: channelDetails?.payCurrency,
              })}
            </Text>
            <Box flex className="flex-row items-center gap-2">
              <Text color="subdued">{ellipseMiddleText(selectedAddress, 6, 6, '*')}</Text>
              <CopyToClipboard text={selectedAddress}>
                <Box className="cursor-pointer flex items-center">
                  <Tooltip position="top" bodyContent={selectedAddress} type="simple">
                    <Button iconElement={<Icon name="Copy" />} variant="ghost" noPadding />
                  </Tooltip>
                </Box>
              </CopyToClipboard>
            </Box>
          </Box>
          <Box className="mt-6">
            <Callout
              variant="info"
              GhostButton={
                <Button
                  onClick={() =>
                    window.open(
                      process.env.SEND_FUNDS_TO_WRONG_NETWORK,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  }
                >
                  {t('learnMore')}
                </Button>
              }
            >
              {channelDetails?.alternatives?.length > 0 ? (
                <Text>
                  {t('payments.channelPayments.callOutWarningProtocol', {
                    currencyCode: channelDetails?.payCurrency,
                    protocol: selectedProtocol || channelDetails?.payCurrency,
                  })}
                </Text>
              ) : (
                <Text>
                  {t('payments.channelPayments.callOutWarningNetwork', {
                    currencyCode: channelDetails?.payCurrency,
                    network: selectedCurrencyNetwork?.name,
                  })}
                </Text>
              )}
            </Callout>
          </Box>
        </Box>
      }
    />
  )
}
